// Visão por Disciplina - Bloco 7
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { GraduationCap, Users, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;

export function VisaoDisciplina({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDisciplina, setSelectedDisciplina] = useState(null);

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/por-disciplina?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { disciplinas, total_disciplinas } = data;

  const getColor = (percentual) => {
    if (percentual >= 90) return { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-600' };
    if (percentual >= 70) return { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-600' };
    return { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-600' };
  };

  return (
    <div className="space-y-6" data-testid="visao-disciplina">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl text-white">
          <GraduationCap className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Visão por Disciplina</h2>
          <p className="text-slate-500 text-sm">{total_disciplinas} disciplinas monitoradas</p>
        </div>
      </div>

      {/* Cards de Disciplinas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {disciplinas.map((disc, idx) => {
          const colors = getColor(disc.percentual_conclusao);
          const isSelected = selectedDisciplina?.id === disc.id;
          
          return (
            <Card 
              key={idx} 
              className={`cursor-pointer transition-all hover:shadow-lg ${colors.bg} ${colors.border} border-2 ${isSelected ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setSelectedDisciplina(isSelected ? null : disc)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-slate-800">{disc.nome}</h3>
                    <p className="text-sm text-slate-500">{disc.total_turmas_pendentes} turmas com pendência</p>
                  </div>
                  <div className={`text-3xl font-bold ${colors.text}`}>
                    {disc.percentual_conclusao}%
                  </div>
                </div>
                
                <Progress 
                  value={disc.percentual_conclusao} 
                  className={`h-2 ${
                    disc.percentual_conclusao >= 90 ? '[&>div]:bg-green-500' :
                    disc.percentual_conclusao >= 70 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                  }`}
                />
                
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="text-slate-600">
                    <span className="text-green-600 font-medium">{disc.total_lancado}</span> / {disc.total_esperado}
                  </span>
                  {disc.pendencias_criticas > 0 && (
                    <Badge className="bg-red-500 text-white">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {disc.pendencias_criticas} críticas
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detalhe da Disciplina Selecionada */}
      {selectedDisciplina && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Detalhes: {selectedDisciplina.nome}</span>
              <Badge className={
                selectedDisciplina.percentual_conclusao >= 90 ? 'bg-green-500' :
                selectedDisciplina.percentual_conclusao >= 70 ? 'bg-yellow-500' : 'bg-red-500'
              }>
                {selectedDisciplina.percentual_conclusao}% concluído
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Turmas Pendentes */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Turmas com Pendências
                </h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedDisciplina.turmas_pendentes.map((turma, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <span className="font-medium text-slate-700">{turma.nome}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          turma.status === 'critico' ? 'bg-red-100 text-red-700' :
                          turma.status === 'atrasado' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }>
                          {turma.pendente} pendentes
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Professores e Tipos Faltantes */}
              <div className="space-y-4">
                {/* Professores */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    Professores Responsáveis
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDisciplina.professores.map((prof, idx) => (
                      <Badge key={idx} variant="outline" className="bg-white">
                        {prof.nome}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Tipos mais faltantes */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3">Tipos Mais Ausentes</h4>
                  <div className="space-y-2">
                    {selectedDisciplina.tipos_mais_faltantes.map((tipo, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-sm">{tipo.tipo}</span>
                        <Badge className="bg-red-100 text-red-700">
                          {tipo.total} faltantes
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
